// src/components/OPDReports.js
import React, { useState, useEffect } from "react";
import { Pie, Bar, Line, Radar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
} from "chart.js";
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Radar as RadarIcon, 
  Download,
  FileText,
  Filter,
  Users,
  Calendar,
  Activity,
  Heart,
  Briefcase,
  Eye,
  Ear,
  Accessibility,
  Brain,
  MessageSquare,
  Plus,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import jsPDF from "jspdf";
const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale
);

const valueLabelsPlugin = {
  id: 'valueLabels',
  afterDatasetsDraw(chart) {
    const type = chart.config?.type;
    if (type !== 'pie' && type !== 'doughnut') return;
    const { ctx, data } = chart;
    const dataset = data?.datasets?.[0];
    if (!dataset) return;
    const meta = chart.getDatasetMeta(0);
    const raw = Array.isArray(dataset.data) ? dataset.data : [];
    const total = raw.reduce((acc, v) => acc + (Number.isFinite(Number(v)) ? Number(v) : 0), 0);
    ctx.save();
    meta.data.forEach((el, i) => {
      const val = Number(dataset.data?.[i]);
      if (!Number.isFinite(val) || val <= 0) return;
      const percent = total > 0 ? Math.round((val / total) * 100) : 0;
      const pos = el.tooltipPosition();
      const fontSize = Math.max(Math.floor(chart.width / 50), 10);
      ctx.fillStyle = '#111827';
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${val} (${percent}%)`, pos.x, pos.y);
    });
    ctx.restore();
  }
};

ChartJS.register(valueLabelsPlugin);

const ageGroups = [
  { label: "Infant (0-1)", min: 0, max: 1 },
  { label: "Early Childhood (2-4)", min: 2, max: 4 },
  { label: "Childhood (5-12)", min: 5, max: 12 },
  { label: "Adolescence (13-17)", min: 13, max: 17 },
  { label: "Youth (18-24)", min: 18, max: 24 },
  { label: "Adult (25-64)", min: 25, max: 64 },
  { label: "Senior (65+)", min: 65, max: 200 },
];

const disabilities = [
  "ዓይነ ስውር",
        "መስማት የተሳነው",
        "የአካል / የመንቀሳቀስ እክል",
        "የአዕምሮ እድገት ውስንነት",
        "ኦቲዝም/የነርቭ ስርዓት እክል",
        "የአእምሮ ጤና እክል",
        "ሥር የሰደደ ሕመም",
        "የመናገር እክል",
        "የተለየ",
];

const genders = ["ወንድ", "ሴት"];
const supportTypes = ["Education", "Health", "Employment", "Other"];

const getDisabilityIcon = (disability) => {
  switch (disability) {
    case "ዓይነ ስውር": return Eye;
    case "መስማት የተሳነው": return Ear;
    case "የአካል / የመንቀሳቀስ እክል": return Accessibility;
    case "የአዕምሮ እድገት ውስንነት": return Brain;
    case "የአእምሮ ጤና እክል": return Heart;
    case "የመናገር እክል": return MessageSquare;
    default: return Plus;
  }
};

const getSupportIcon = (support) => {
  switch (support) {
    case "Education": return Brain;
    case "Health": return Heart;
    case "Employment": return Briefcase;
    default: return Plus;
  }
};

const OPDReports = () => {
  const [analysisType, setAnalysisType] = useState("Age");
  const [displayType, setDisplayType] = useState("Pie");
  const [chartData, setChartData] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedDisability, setSelectedDisability] = useState("");
  const [selectedSupport, setSelectedSupport] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [opdSubCity, setOpdSubCity] = useState("");

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  // Fetch members for logged-in OPD from backend by sub city
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        const currentUserEmail = currentUser?.email;
        if (!currentUserEmail) {
          setError("No logged-in OPD found.");
          return;
        }

        // Fetch OPD profile to get sub city
        let subCity = opdSubCity;
        if (!subCity) {
          const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
          const profData = await profRes.json();
          if (profData?.status === 'success' && profData?.data?.sub_city) {
            subCity = profData.data.sub_city;
            setOpdSubCity(subCity);
          } else {
            setMembers([]);
            setLoading(false);
            return;
          }
        }

        // Fetch all members from getAllMembers.php
        const res = await fetch(`${API_BASE_URL}/getAllMembers.php`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        
        // Filter by sub city and approved status
        const norm = (s) => (s || '').toString().trim().toLowerCase();
        const subCityNorm = norm(subCity);
        const filtered = list.filter(m => 
          norm(m.subCity) === subCityNorm && 
          norm(m.aaapd_status) === 'approved'
        );
        setMembers(filtered);
        setError("");
      } catch (err) {
        setError("Failed to fetch member data.");
        console.error("Error fetching members:", err);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = members;
    if (selectedGender) filtered = filtered.filter((m) => m.gender === selectedGender);
    if (selectedDisability) {
      const target = selectedDisability.toLowerCase();
      filtered = filtered.filter((m) => {
        const memberTypes = (m.disabilityType || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        return memberTypes.includes(target);
      });
    }
    if (selectedSupport) filtered = filtered.filter((m) => m.supportNeeded === selectedSupport);

    let counts = {};
    if (analysisType === "Age") {
      counts = ageGroups.reduce((acc, group) => ({ ...acc, [group.label]: 0 }), {});
      filtered.forEach((m) => {
        const n = Number(m.age);
        const age = Number.isFinite(n) && n > 0 ? n : null;
        if (age !== null) {
          const group = ageGroups.find((g) => age >= g.min && age <= g.max);
          if (group) counts[group.label]++;
        }
      });
    } else if (analysisType === "Gender") {
      counts = genders.reduce((acc, g) => ({ ...acc, [g]: 0 }), {});
      filtered.forEach((m) => (counts[m.gender] = (counts[m.gender] || 0) + 1));
    } else if (analysisType === "Disability Type") {
      // Initialize all known categories to 0
      counts = disabilities.reduce((acc, d) => ({ ...acc, [d]: 0 }), {});
      // For each member, split comma-separated disabilityType and count each matching category
      filtered.forEach((m) => {
        const memberTypes = (m.disabilityType || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        disabilities.forEach((cat) => {
          const target = cat.toLowerCase();
          if (memberTypes.includes(target)) {
            counts[cat] = (counts[cat] || 0) + 1;
          }
        });
        // If no known type matched but there is some value, count as Other
        if (memberTypes.length > 0) {
          const knowns = disabilities.map((d) => d.toLowerCase());
          const hasKnown = memberTypes.some((t) => knowns.includes(t));
          if (!hasKnown) counts["Other"] = (counts["Other"] || 0) + 1;
        }
      });
    } else if (analysisType === "Support Needed") {
      counts = supportTypes.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
      filtered.forEach((m) => {
        const support = m.supportNeeded || "Other";
        counts[support] = (counts[support] || 0) + 1;
      });
    }

    const labels = Object.keys(counts);
    const data = Object.values(counts);

    setChartData({
      labels,
      datasets: [
        {
          label: analysisType,
          data,
          backgroundColor: [
            "rgba(20, 184, 166, 0.8)",
            "rgba(0, 95, 248, 0.8)",
            "rgba(234, 234, 14, 0.8)",
            "rgba(245, 158, 11, 0.8)",
            "rgba(255, 0, 0, 0.8)",
            "rgba(241, 108, 108, 0.8)",
            "rgba(139, 92, 246, 0.8)",
            "rgba(6, 182, 212, 0.8)",
            "rgba(132, 204, 22, 0.8)",
            "rgba(236, 72, 153, 0.8)",
          ],
          borderColor: [
            "rgb(20, 184, 166)",
            "rgb(59, 130, 246)",
            "rgba(254, 254, 0, 0.8)",
            "rgb(245, 158, 11)",
            "rgba(255, 0, 0, 0.8)",
            "rgb(239, 68, 68)",
            "rgb(139, 92, 246)",
            "rgb(6, 182, 212)",
            "rgb(132, 204, 22)",
            "rgb(236, 72, 153)",
          ],
          borderWidth: 2,
          borderRadius: 6,
        },
      ],
    });
  }, [members, analysisType, selectedGender, selectedDisability, selectedSupport]);

  const getChartIcon = () => {
    switch (displayType) {
      case "Bar": return BarChart3;
      case "Line": return TrendingUp;
      case "Radar": return RadarIcon;
      case "Doughnut": return PieChart;
      default: return PieChart;
    }
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            <span className="text-gray-600">Loading chart data...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="w-6 h-6" />
            <span>{error}</span>
          </div>
        </div>
      );
    }

    if (!chartData) {
      return (
        <div className="flex items-center justify-center h-full">
          <span className="text-gray-500">No data available</span>
        </div>
      );
    }

    const options = { 
      responsive: true, 
      maintainAspectRatio: false,
      aspectRatio: window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 1.5 : 2,
      plugins: {
        legend: {
          position: window.innerWidth < 640 ? 'bottom' : 'bottom',
          labels: {
            padding: window.innerWidth < 640 ? 10 : 20,
            usePointStyle: true,
            font: {
              size: window.innerWidth < 640 ? 10 : window.innerWidth < 1024 ? 11 : 12
            },
            boxWidth: window.innerWidth < 640 ? 12 : 15,
            boxHeight: window.innerWidth < 640 ? 12 : 15
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(20, 184, 166, 0.8)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          titleFont: {
            size: window.innerWidth < 640 ? 12 : 14
          },
          bodyFont: {
            size: window.innerWidth < 640 ? 11 : 13
          }
        }
      },
      scales: displayType === 'Bar' || displayType === 'Line' ? {
        x: {
          ticks: {
            font: {
              size: window.innerWidth < 640 ? 10 : 12
            },
            maxRotation: window.innerWidth < 640 ? 45 : 0
          }
        },
        y: {
          ticks: {
            font: {
              size: window.innerWidth < 640 ? 10 : 12
            }
          }
        }
      } : {}
    };

    switch (displayType) {
      case "Bar":
        return <Bar data={chartData} options={options} />;
      case "Line":
        return <Line data={chartData} options={options} />;
      case "Radar":
        return <Radar data={chartData} options={options} />;
      case "Doughnut":
        return <Doughnut data={chartData} options={options} />;
      default:
        return <Pie data={chartData} options={options} />;
    }
  };

  const downloadChart = () => {
    const canvas = document.querySelector("canvas");
    if (!canvas) {
      showNotification("No chart available to download", "error");
      return;
    }
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${analysisType}_${displayType}_chart.png`;
    link.click();
    showNotification("Chart downloaded successfully!", "success");
  };

  // Generate PDF only for logged-in OPD
  const downloadPDF = async () => {
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Title page
      pdf.setFontSize(22);
      pdf.text("OPD Members Report & Analysis", pageWidth / 2, 60, { align: "center" });
      pdf.setFontSize(14);
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 80, { align: "center" });
      pdf.addPage();

      // Summary statistics page
      const totalMembers = members.length;
      const maleCount = members.filter((m) => m.gender === "ወንድ").length;
      const femaleCount = members.filter((m) => m.gender === "ሴት").length;
      const avgAge = Math.round(
        members.reduce((acc, m) => acc + (calculateAge(m.dateOfBirth) || 0), 0) /
          (members.length || 1)
      );

      pdf.setFontSize(18);
      pdf.text("Summary Statistics", 14, 30);
      pdf.setFontSize(12);
      pdf.text(`Total Members: ${totalMembers}`, 14, 50);
      pdf.text(`ወንድ / ሴት: ${maleCount} / ${femaleCount}`, 14, 65);
      pdf.text(`Average Age: ${avgAge}`, 14, 80);

      pdf.addPage();

      // Chart page
      const chartCanvas = document.querySelector("canvas");
      if (chartCanvas) {
        const chartImg = chartCanvas.toDataURL("image/png", 1.0);
        const imgProps = pdf.getImageProperties(chartImg);
        const pdfHeight = (imgProps.height * pageWidth) / imgProps.width;
        pdf.setFontSize(18);
        pdf.text(`${analysisType} Distribution`, 14, 20);
        pdf.addImage(chartImg, "PNG", 0, 30, pageWidth, pdfHeight);
      }

      pdf.save(`OPD_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      showNotification("PDF report generated successfully!", "success");
    } catch (err) {
      showNotification("Failed to generate PDF report", "error");
      console.error("PDF generation error:", err);
    }
  };

  // Statistics calculations
  const totalMembers = members.length;
  const maleCount = members.filter((m) => m.gender === "ወንድ").length;
  const femaleCount = members.filter((m) => m.gender === "ሴት").length;
  const validAges = members.map(m => Number(m.age)).filter(a => Number.isFinite(a) && a > 0);
  const avgAge = validAges.length > 0 ? Math.round(validAges.reduce((a,b)=>a+b,0)/validAges.length) : 0;

  const ChartIcon = getChartIcon();

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-2 right-2 sm:top-4 sm:right-4 z-50 flex items-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 rounded-lg shadow-lg max-w-xs sm:max-w-sm ${
          notification.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
        }`}>
          {notification.type === "success" ? (
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          )}
          <span className="text-sm sm:text-base truncate">{notification.message}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-4 py-6 sm:px-6 sm:py-8 text-white">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">OPD Reports & Analytics</h1>
                <p className="text-teal-100 mt-1 text-sm sm:text-base hidden sm:block">Comprehensive analysis and insights for your organization</p>
                <p className="text-teal-100 mt-1 text-xs sm:hidden">Analysis & insights for your OPD</p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Members</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{totalMembers}</p>
              </div>
              <div className="bg-teal-100 p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-teal-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">ወንድ / ሴት</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{maleCount} / {femaleCount}</p>
              </div>
              <div className="bg-blue-100 p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Average Age</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{avgAge} years</p>
              </div>
              <div className="bg-green-100 p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Chart Type</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{displayType}</p>
              </div>
              <div className="bg-purple-100 p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2">
                <ChartIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-3 sm:mb-4">
            <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Filters & Analysis Options</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Analysis Type</label>
              <select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              >
                <option value="Age">Age Distribution</option>
                <option value="Gender">Gender Distribution</option>
                <option value="Disability Type">Disability Types</option>
                <option value="Support Needed">Support Categories</option>
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Chart Type</label>
              <select
                value={displayType}
                onChange={(e) => setDisplayType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              >
                <option value="Pie">Pie Chart</option>
                <option value="Bar">Bar Chart</option>
                <option value="Line">Line Chart</option>
                <option value="Radar">Radar Chart</option>
                <option value="Doughnut">Doughnut Chart</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Gender Filter</label>
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              >
                <option value="">All Genders</option>
                {genders.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Disability Filter</label>
              <select
                value={selectedDisability}
                onChange={(e) => setSelectedDisability(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              >
                <option value="">ሁሉም የአካል ጉዳት ዓይነቶች</option>
                {disabilities.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Support Filter</label>
              <select
                value={selectedSupport}
                onChange={(e) => setSelectedSupport(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              >
                <option value="">All Support Types</option>
                {supportTypes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <ChartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 flex-shrink-0" />
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                <span className="hidden sm:inline">{analysisType} Analysis - {displayType} Chart</span>
                <span className="sm:hidden">{analysisType} - {displayType}</span>
              </h2>
            </div>
            
            <div className="flex space-x-2 sm:space-x-3">
              <button
                onClick={downloadChart}
                disabled={loading || !chartData}
                className="flex items-center space-x-1 sm:space-x-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition-colors text-sm sm:text-base"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Chart</span>
                <span className="sm:hidden">PNG</span>
              </button>
              <button
                onClick={downloadPDF}
                disabled={loading || !chartData}
                className="flex items-center space-x-1 sm:space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition-colors text-sm sm:text-base"
              >
                <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">PDF Report</span>
                <span className="sm:hidden">PDF</span>
              </button>
            </div>
          </div>
          
          <div className="relative w-full h-[300px] sm:h-[400px] lg:h-[500px]">
            {renderChart()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPDReports;
